import { QuickPassUser } from '../_types/quickpass-user'
import { QuickPassProfile } from '../_types/quickpass-profile'
import { defaultQuickPassProfile } from '../_defaults/quickpass-profile'
import { readUser, writeProfile, writeUser } from './local-store'

const DEV_BYPASS_FLAG_STORAGE_KEY = 'quickpass:dev:bypassAuth'
const DEV_BYPASS_FIREBASE_UID = 'dev_bypass_synthetic_user'
const DEV_BYPASS_EMAIL = 'dev-renter@quickpass.local'
const DEV_BYPASS_DISPLAY_NAME = 'Test Renter'

/**
 * Reports whether the dev-mode auth bypass is currently active. Only ever
 * returns true when both the build is `import.meta.env.DEV` AND the
 * sentinel flag has been explicitly set in localStorage. Production
 * builds short-circuit at the env check.
 *
 * @returns {boolean} true when the dev bypass should override real Firebase Auth
 */
const isDevAuthBypassEnabled = (): boolean => {
  if (import.meta.env.DEV !== true) {
    return false
  }

  try {
    return window.localStorage.getItem(DEV_BYPASS_FLAG_STORAGE_KEY) === '1'
  } catch (error) {
    console.warn('[dev-bypass] failed to read flag', error)
    return false
  }
}

/**
 * Builds a stub `FirebaseUser`-shaped object for the auth context to use
 * when the dev bypass is on. Only the fields the context actually reads
 * (uid, email, phoneNumber, displayName, providerData) are populated;
 * unused Firebase methods are stubbed to throw so accidental reliance
 * surfaces loudly in dev.
 *
 * @returns {object} a synthetic FirebaseUser proxy
 */
const buildSyntheticFirebaseUser = (): {
  uid: string
  email: string
  phoneNumber: string | null
  displayName: string | null
  providerData: Array<{ providerId: string }>
} => {
  return {
    uid: DEV_BYPASS_FIREBASE_UID,
    email: DEV_BYPASS_EMAIL,
    phoneNumber: null,
    displayName: DEV_BYPASS_DISPLAY_NAME,
    providerData: [{ providerId: 'password' }],
  }
}

/**
 * Builds a fully-onboarded synthetic QuickPassUser + QuickPassProfile and
 * seeds them into the local store so the rest of the app sees a
 * consistent renter on first paint.
 *
 * @returns {void}
 */
const seedSyntheticIdentity = (): void => {
  const now = Date.now()
  const stableId = `qp_user_${DEV_BYPASS_FIREBASE_UID}`

  const user: QuickPassUser = {
    id: stableId,
    firebaseAuthUid: DEV_BYPASS_FIREBASE_UID,
    quickCasaUserId: '',
    email: DEV_BYPASS_EMAIL,
    phone: null,
    displayName: DEV_BYPASS_DISPLAY_NAME,
    authProviders: ['email-password'],
    pushTokens: [],
    createdAt: now,
    updatedAt: now,
    lastSignInAt: now,
  }

  const profile: QuickPassProfile = {
    ...defaultQuickPassProfile,
    id: stableId,
    quickPassUserId: stableId,
    firstName: 'Taylor',
    lastName: 'Renter',
    unitPreferences: {
      ...defaultQuickPassProfile.unitPreferences,
      cities: ['Toronto', 'Hamilton'],
      priceMin: 1800,
      priceMax: 2800,
      bedroomCounts: [1, 2],
      bathroomCounts: [1],
      propertyTypes: ['apartment', 'condo'],
      dwellingScope: 'unit',
    },
    onboardingCompletedAt: now,
    createdAt: now,
    updatedAt: now,
  }

  writeUser(user)
  writeProfile(profile)
}

/**
 * Turns the dev-mode auth bypass on, seeds the synthetic identity, and
 * forces a page reload so the auth context picks up the change cleanly.
 *
 * @returns {void}
 */
const enableDevAuthBypass = (): void => {
  if (import.meta.env.DEV !== true) {
    console.warn('[dev-bypass] refused to enable in non-dev build')
    return
  }

  try {
    window.localStorage.setItem(DEV_BYPASS_FLAG_STORAGE_KEY, '1')
    seedSyntheticIdentity()
    window.location.reload()
  } catch (error) {
    console.error('[dev-bypass] failed to enable', error)
  }
}

/**
 * Turns the dev-mode auth bypass off and reloads. Leaves seeded
 * localStorage entries intact so the renter's preferences are preserved
 * if the user re-enables the bypass later.
 *
 * @returns {void}
 */
const disableDevAuthBypass = (): void => {
  try {
    window.localStorage.removeItem(DEV_BYPASS_FLAG_STORAGE_KEY)
    window.location.reload()
  } catch (error) {
    console.error('[dev-bypass] failed to disable', error)
  }
}

/**
 * Synchronously reads (or seeds) the synthetic QuickPassUser used by the
 * bypass. Lets the auth context skip the async fetch entirely when the
 * bypass is on, avoiding the transitional render where firebaseUser is
 * set but quickPassUser is not.
 *
 * @returns {QuickPassUser} the synthetic user, fresh-seeded if missing
 */
const resolveSyntheticQuickPassUserSync = (): QuickPassUser => {
  const existing = readUser(DEV_BYPASS_FIREBASE_UID)

  if (existing !== null) {
    return existing
  }

  seedSyntheticIdentity()
  const seeded = readUser(DEV_BYPASS_FIREBASE_UID)
  if (seeded !== null) {
    return seeded
  }

  const now = Date.now()
  return {
    id: `qp_user_${DEV_BYPASS_FIREBASE_UID}`,
    firebaseAuthUid: DEV_BYPASS_FIREBASE_UID,
    quickCasaUserId: '',
    email: DEV_BYPASS_EMAIL,
    phone: null,
    displayName: DEV_BYPASS_DISPLAY_NAME,
    authProviders: ['email-password'],
    pushTokens: [],
    createdAt: now,
    updatedAt: now,
    lastSignInAt: now,
  }
}

export {
  buildSyntheticFirebaseUser,
  disableDevAuthBypass,
  enableDevAuthBypass,
  isDevAuthBypassEnabled,
  resolveSyntheticQuickPassUserSync,
}
